import { useEffect, useState } from 'react';
import { findMyActiveSearch, formatSearchClock } from 'react/matchmaking/unified/header/myActiveSearch';

// The player's own search clock, shared by the header's Play status, the
// "right now" box and the LFG card. One ticker reads the search once a second
// for every reader, so they always agree.
//
// The countdown never jumps back up on a stale update. The server shortens a
// search when someone challenges it (match_starter.php), and an older copy of
// the search can still arrive afterwards. A longer time is shown once it has
// held for a few seconds (the timer was restarted in another tab), or at once
// after this tab restarted the timer.

const CLOCK_INTERVAL_MS = 1000;
const DEFAULT_CONFIRM_INCREASE_AFTER_MS = 3000;
// Re-reads of the same end time differ by a few milliseconds.
const END_TIME_TOLERANCE_MS = 1000;

// When the search ends (ms), true for no time limit, null when unknown.
function searchEndTime(search, now) {
	if(search.searchTimeEnd === true) {
		return true;
	}
	if(search.searchTimeEnd) {
		return search.searchTimeEnd.valueOf();
	}
	if(typeof search.getSearchTimeRemaining === 'function') {
		const secondsRemaining = search.getSearchTimeRemaining();
		return secondsRemaining === true ? true : now + secondsRemaining * 1000;
	}
	return null;
}

export function createSearchCountdown({ confirmIncreaseAfterMs = DEFAULT_CONFIRM_INCREASE_AFTER_MS } = {}) {
	let searchId = null;
	let shownEndTime = null;
	let longerEndTimeSince = null;
	const restartedSearchIds = new Set();

	function reset() {
		searchId = null;
		shownEndTime = null;
		longerEndTimeSince = null;
	}

	function acceptEndTime(endTime) {
		shownEndTime = endTime;
		longerEndTimeSince = null;
	}

	return {
		reset() {
			reset();
			restartedSearchIds.clear();
		},

		markRestarted(restartedSearchId) {
			restartedSearchIds.add(String(restartedSearchId));
		},

		// Whole seconds left on `search` at `now` (ms), true for no time limit, null without a search.
		secondsRemaining(search, now = Date.now()) {
			if(!search) {
				reset();
				return null;
			}
			const endTime = searchEndTime(search, now);
			if(endTime === true || endTime === null) {
				reset();
				return endTime;
			}
			const currentSearchId = String(search.id);
			if(currentSearchId !== searchId || shownEndTime === null) {
				searchId = currentSearchId;
				acceptEndTime(endTime);
			}
			else if(endTime <= shownEndTime + END_TIME_TOLERANCE_MS) {
				acceptEndTime(Math.min(endTime, shownEndTime));
			}
			else if(restartedSearchIds.has(currentSearchId)) {
				restartedSearchIds.delete(currentSearchId);
				acceptEndTime(endTime);
			}
			else {
				if(longerEndTimeSince === null) {
					longerEndTimeSince = now;
				}
				if(now - longerEndTimeSince >= confirmIncreaseAfterMs) {
					acceptEndTime(endTime);
				}
			}
			return Math.max(0, Math.floor((shownEndTime - now) / 1000));
		},
	};
}

const mySearchCountdown = createSearchCountdown();
const listeners = new Set();
let currentReading = null;
let clockInterval = null;

function readMySearchClock() {
	const search = findMyActiveSearch();
	const secondsRemaining = mySearchCountdown.secondsRemaining(search, Date.now());
	if(!search || secondsRemaining === null) {
		return { searchId: null, clock: null };
	}
	return { searchId: String(search.id), clock: formatSearchClock(secondsRemaining) };
}

function tick() {
	currentReading = readMySearchClock();
	listeners.forEach((listener) => listener(currentReading));
}

function subscribe(listener) {
	listeners.add(listener);
	if(!clockInterval) {
		clockInterval = setInterval(tick, CLOCK_INTERVAL_MS);
	}
	return () => {
		listeners.delete(listener);
		if(!listeners.size) {
			clearInterval(clockInterval);
			clockInterval = null;
			currentReading = null;
		}
	};
}

function latestReading() {
	if(!currentReading || !clockInterval) {
		currentReading = readMySearchClock();
	}
	return currentReading;
}

/** Call when this tab restarts the search timer, so the longer time shows at once. */
export function markSearchTimerRestarted(searchId) {
	mySearchCountdown.markRestarted(searchId);
}

/** For tests: forget the countdown and the shared reading. */
export function resetMySearchCountdown() {
	mySearchCountdown.reset();
	currentReading = null;
}

/** { searchId, clock }: the player's own search and its m:ss clock, or nulls when not searching. */
export function useMySearchClock() {
	const [reading, setReading] = useState(latestReading);
	useEffect(() => subscribe(setReading), []);
	return reading;
}
